/**
 * useAuth — slice-scope hook that exposes the dev-paste token. Production
 * replaces this with a real service-identity hook (separate ticket).
 *
 * The hook subscribes to a custom `upmp:token-changed` event so other tabs /
 * components can react to login/logout without needing a full state lib.
 */
import { useCallback, useEffect, useState } from 'react';

import { clearToken, getToken, setToken as writeToken } from './tokenStore';

const TOKEN_CHANGED_EVENT = 'upmp:token-changed';

interface UseAuthResult {
  token: string | null;
  setToken: (token: string) => void;
  isAuthenticated: boolean;
  logout: () => void;
}

export function useAuth(): UseAuthResult {
  const [token, setTokenState] = useState<string | null>(() => getToken());

  useEffect(() => {
    const onChange = (): void => {
      setTokenState(getToken());
    };
    window.addEventListener(TOKEN_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(TOKEN_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const setToken = useCallback((next: string): void => {
    writeToken(next);
    window.dispatchEvent(new Event(TOKEN_CHANGED_EVENT));
    setTokenState(next);
  }, []);

  const logout = useCallback((): void => {
    clearToken();
    window.dispatchEvent(new Event(TOKEN_CHANGED_EVENT));
    setTokenState(null);
  }, []);

  return {
    token,
    setToken,
    isAuthenticated: token !== null,
    logout,
  };
}

export { TOKEN_CHANGED_EVENT };
